import { zodResolver } from '@hookform/resolvers/zod';
import {
	Button,
	Dialog,
	DialogClose,
	DialogContent,
	DialogDescription,
	DialogFooter,
	DialogHeader,
	DialogTitle,
	Form,
	FormControl,
	FormField,
	FormItem,
	FormLabel,
	FormMessage,
	Input,
} from '@uturn/ui-kit';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { type LeadFormFields, leadFormSchema } from '../schema';

export function PriceRequestLeadForm({
	onSubmit,
	openModal,
	setOpenModal,
}: {
	openModal: boolean;
	setOpenModal: (open: boolean) => void;
	onSubmit: (email: string) => void;
}) {
	const { t } = useTranslation();

	const form = useForm<LeadFormFields>({
		resolver: zodResolver(leadFormSchema),
		mode: 'onChange',
		defaultValues: { email: '' },
	});

	const handleSubmit = (data: LeadFormFields) => {
		onSubmit(data.email);
		setOpenModal(false);
		form.reset();
	};

	const handleOpenChange = (open: boolean) => {
		if (!open) {
			form.reset();
		}
		setOpenModal(open);
	};

	return (
		<Dialog open={openModal} onOpenChange={handleOpenChange}>
			<DialogContent>
				<Form {...form}>
					<form
						onSubmit={form.handleSubmit(handleSubmit)}
						className="space-y-4"
					>
						<DialogHeader>
							<DialogTitle>
								{t('pages.price-calculator.modal.title', 'Request price')}
							</DialogTitle>
							<DialogDescription>
								{t(
									'pages.price-calculator.body',
									"Just leave your email address below and we'll get back to you as soon as possible.",
								)}
							</DialogDescription>
						</DialogHeader>
						<FormField
							control={form.control}
							name="email"
							render={({ field }) => (
								<FormItem>
									<FormLabel>{t('general-email', 'E-mail')}</FormLabel>
									<FormControl>
										<Input
											type="email"
											placeholder="name@company.com"
											{...field}
										/>
									</FormControl>
									<FormMessage />
								</FormItem>
							)}
						/>
						<DialogFooter>
							<DialogClose asChild>
								<Button type="button" variant="outline">
									{t('pages.price-calculator.modal.close', 'Cancel')}
								</Button>
							</DialogClose>
							<Button type="submit" disabled={!form.formState.isValid}>
								{t('pages.price-calculator.modal.lead-send', 'Let’s go!')}
							</Button>
						</DialogFooter>
					</form>
				</Form>
			</DialogContent>
		</Dialog>
	);
}
